import React, { useState } from "react";
import { HelpCircleIcon, Loader2, MailIcon } from "lucide-react";
import { Button } from "@/app/components/ui/button.js";
import { Card, CardContent } from "@/app/components/ui/card.jsx";
import { Input } from "@/app/components/ui/input.jsx";
import { useCompanyClaim } from "@/app/hooks/useCompanyClaim.js";
import { ClaimModal } from "@/app/Views/Companies/CompanyClaim/sections/CalimModal/ClaimModal.jsx";
import { toast } from "sonner";

const steps = [
	{
		number: "1",
		icon: "/images/claim-landing/group-11-2.svg",
		title: "Enter Your Work Email",
		description: "name@abclogistics.co.tz",
		numberLeft: "left-[284px]",
	},
	{
		number: "2",
		icon: "/images/claim-landing/group-11.svg",
		title: "Verify with OTP",
		description: "Get an Instant code to your inbox.",
		numberLeft: "left-[272px]",
	},
	{
		number: "3",
		icon: "/images/claim-landing/group-11-1.svg",
		title: "Takeover",
		description: "Admin access unlocked.",
		numberLeft: "left-[272px]",
	},
];

export const CallToActionSection = ({ companyName, companyId, isCompanyClaimed }) => {
	const [email, setEmail] = useState("");
	const [isClaimModalOpen, setIsClaimModalOpen] = useState(false);
	const { requestClaimOTP, isLoading } = useCompanyClaim();

	const handleSendVerification = async () => {
		if (!email) {
			toast.error("Please enter your company email address.");
			return;
		}

		const payload = {
			companyId: companyId,
			email: email,
		};

		const success = await requestClaimOTP(payload);
		if (success) {
			setEmail("");
			setIsClaimModalOpen(true);
		}
	};

	return (
		<section className="flex flex-col items-start gap-6 p-20 w-full">
			<div className="w-full flex items-start justify-center gap-12">
				<div className="gap-12 flex flex-col items-center flex-1">
					<div className="flex flex-col items-center gap-3 w-full translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:200ms]">
						{isCompanyClaimed ? (
							<h2 className="[font-family:'Plus_Jakarta_Sans',Helvetica] font-bold text-anima-dark-text text-3xl md:text-4xl tracking-[0] leading-[62.4px] whitespace-nowrap">
								Login to your page in 30 seconds.
							</h2>
						) : (
							<h2 className="[font-family:'Plus_Jakarta_Sans',Helvetica] font-bold text-anima-dark-text text-3xl md:text-4xl tracking-[0] leading-[62.4px] whitespace-nowrap">
								Secure your page in 30 seconds.
							</h2>
						)}
					</div>

					<div className="flex flex-col w-full max-w-[1281px] items-start gap-8 translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:400ms]">
						<div className="relative w-full h-[358px]">
							<div className="absolute top-0 left-0 w-[340px] h-[258px] rounded-lg overflow-hidden">
								<div className="flex flex-col w-72 items-center gap-6 absolute top-10 left-[26px]">
									<img className="w-[60px] h-[60px]" alt="Group" src={steps[0].icon} />

									<div className="flex flex-col items-center gap-3 w-full">
										<h3 className="[font-family:'Inter',Helvetica] font-medium text-black text-xl text-center tracking-[0] leading-9">
											{steps[0].title}
										</h3>

										<p className="[font-family:'Inter',Helvetica] font-normal text-anima-gray-text-2 text-xl text-center tracking-[0] leading-[30px]">
											{steps[0].description}
										</p>
									</div>
								</div>

								<div
									className={`absolute top-8 ${steps[0].numberLeft} [font-family:'Inter',Helvetica] font-semibold text-anima-gray-text-3 text-[64px] tracking-[0] leading-[96px] whitespace-nowrap`}
								>
									{steps[0].number}
								</div>
							</div>

							<div className="top-[50px] left-[460px] absolute w-[340px] h-[258px]">
								<div className="flex flex-col w-72 items-center gap-6 absolute top-10 left-[26px]">
									<img className="w-[60px] h-[60px]" alt="Group" src={steps[1].icon} />

									<div className="flex flex-col items-center gap-3 w-full">
										<h3 className="[font-family:'Inter',Helvetica] font-medium text-black text-xl text-center tracking-[0] leading-9">
											{steps[1].title}
										</h3>

										<p className="[font-family:'Inter',Helvetica] font-normal text-anima-gray-text-2 text-xl text-center tracking-[0] leading-[30px]">
											{steps[1].description}
										</p>
									</div>
								</div>

								<div
									className={`absolute top-8 ${steps[1].numberLeft} [font-family:'Inter',Helvetica] font-semibold text-anima-gray-text-3 text-[64px] tracking-[0] leading-[96px] whitespace-nowrap`}
								>
									{steps[1].number}
								</div>
							</div>

							<div className="top-0 left-[920px] absolute w-[340px] h-[258px]">
								<div className="flex flex-col w-72 items-center gap-6 absolute top-10 left-[26px]">
									<img className="w-[60px] h-[60px]" alt="Group" src={steps[2].icon} />

									<div className="flex flex-col items-center gap-3 w-full">
										<h3 className="[font-family:'Inter',Helvetica] font-medium text-black text-xl text-center tracking-[0] leading-9">
											{steps[2].title}
										</h3>

										<p className="[font-family:'Inter',Helvetica] font-normal text-anima-gray-text-2 text-xl text-center tracking-[0] leading-[30px]">
											{steps[2].description}
										</p>
									</div>
								</div>

								<div
									className={`absolute top-8 ${steps[2].numberLeft} [font-family:'Inter',Helvetica] font-semibold text-anima-gray-text-3 text-[64px] tracking-[0] leading-[96px] whitespace-nowrap`}
								>
									{steps[2].number}
								</div>
							</div>

							<img
								className="absolute top-[77px] left-[375px] w-[134px] h-[33px]"
								alt="Vector"
								src="/images/claim-landing/vector-1.svg"
							/>

							<img
								className="absolute top-[239px] left-[831px] w-[125px] h-[21px]"
								alt="Vector"
								src="/images/claim-landing/vector-2.svg"
							/>
						</div>
					</div>
				</div>
			</div>

			<Card className="w-full max-w-7xl mx-auto bg-primary-100 rounded-3xl border border-solid border-gray-50 translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:600ms]">
				<CardContent className="flex flex-col items-start gap-2.5 px-0 py-10">
					<div className="flex flex-col items-start gap-12 w-full">
						<div className="flex flex-col items-center gap-8 w-full">
							<h2 className="w-full max-w-[903px] [font-family:'Plus_Jakarta_Sans',Helvetica] font-bold text-4xl md:text-5xl text-center tracking-[0] leading-[78px]">
								{isCompanyClaimed ? (
									<span className="text-anima-dark-text">Ready to Login to</span>
								) : (
									<span className="text-anima-dark-text">Ready to Claim </span>
								)}
								<span className="text-primary-700">{companyName}?</span>
							</h2>
						</div>

						<div className="flex flex-col items-center gap-6 w-full">
							<div className="inline-flex items-start gap-3">
								<div className="flex w-[599px] items-center gap-2 rounded-lg">
									<div className="flex-col items-start flex-1 flex">
										<div className="flex flex-col items-start gap-1.5 w-full">
											<div className="flex flex-col items-start gap-1.5 w-full">
												<div className="items-center gap-2 px-3.5 py-3.5 w-full bg-white rounded-lg overflow-hidden shadow-shadow-xs flex">
													<div className="flex items-center gap-2 flex-1">
														<MailIcon className="w-5 h-5 text-gray-500" />
														<Input
															type="email"
															placeholder="Enter company email address"
															className="flex-1 border-0 shadow-none p-0 h-auto font-text-md-regular"
															style={{ background: "none" }}
															value={email}
															onChange={(e) => setEmail(e.target.value)}
														/>
													</div>
													<HelpCircleIcon className="w-4 h-4 text-gray-400" />
												</div>
											</div>
										</div>
									</div>
								</div>

								<Button
									onClick={handleSendVerification}
									disabled={!email || isLoading}
									className="inline-flex items-center justify-center gap-3 px-6 py-[14px] h-auto bg-primary-700 rounded-lg shadow-[0px_2px_8px_#0000001a] hover:bg-primary-700/90 transition-colors min-w-[200px]"
								>
									{isLoading ? (
										<>
											<Loader2 className="w-5 h-5 animate-spin" />
											Sending...
										</>
									) : (
										"Send Verification Code"
									)}
								</Button>
							</div>
						</div>
					</div>
				</CardContent>
			</Card>

			<ClaimModal
				open={isClaimModalOpen}
				onOpenChange={setIsClaimModalOpen}
				companyId={companyId}
				companyName={companyName}
				initialEmail={email}
				initialStep="verification"
			/>
		</section>
	);
};
